/**
 * Supabase client singleton.
 *
 * Reads VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY from your `.env.local`
 * file at build time. The anon key is safe to ship to the browser — Row-Level
 * Security policies on each table enforce per-user data isolation.
 */
import { createClient, type SupabaseClient, type User } from "@supabase/supabase-js";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  // Surface a hard error in the console so we don't silently produce a broken build.
  // The auth gate will also show a friendly message in the UI.
  // eslint-disable-next-line no-console
  console.error(
    "[supabase] Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY. Copy `.env.example` to `.env.local` and fill in your project credentials, then restart the dev server."
  );
}

export const sb: SupabaseClient = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

/** Module-level cache of the signed-in user (or null if signed out). */
export let CLOUD_USER: User | null = null;

/** Setter used by the auth bootstrap; views should treat CLOUD_USER as read-only. */
export function setCloudUser(u: User | null) {
  CLOUD_USER = u;
}
